'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';

import { faLightbulb } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Notification, Text } from '@mantine/core';

import { tipsLinks } from '@repo/core/constants';

import { useAppDisplayRequestErrors } from '@hooks/useAppDisplayRequestErrors';

import { useAppContext } from '@context/AppContext';
import { useHateoasContext } from '@context/HateoasContext';

import { boondFindElementById } from '@utils/functions';
import myFetch from '@utils/myFetch';

export default function TipsNotification() {
    const t = useTranslations();
    const locale = useLocale();
    const [tips, setTips] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [slideDir, setSlideDir] = useState(null);
    const [tipNotification] = useAppContext('tipNotification');
    const { getRel } = useHateoasContext();
    const pathname = usePathname();
    const displayRequestErrors = useAppDisplayRequestErrors();
    const fetchedLinks = useRef(new Set());

    useEffect(() => {
        if (pathname === '/login') return;

        const pendingLinks = tipNotification.pendingLinks || [];
        const legacyLink = tipNotification.link;
        const allLinks = legacyLink && !pendingLinks.includes(legacyLink)
            ? [...pendingLinks, legacyLink]
            : pendingLinks;

        for (const link of allLinks) {
            if (!fetchedLinks.current.has(link) && boondFindElementById(tipsLinks, link)) {
                fetchedLinks.current.add(link);
                fetchTip(link);
            }
        }

        if (!tipNotification.show && tipNotification.link === '' && pendingLinks.length === 0) {
            setTips([]);
            setCurrentIndex(0);
            fetchedLinks.current.clear();
        }
    }, [tipNotification.pendingLinks, tipNotification.link, tipNotification.show, pathname]);


    const fetchTip = async (link) => {
        const tipReadAllLink = getRel('tip:read:all', ['app']);
        if (!tipReadAllLink?.href || !tipReadAllLink?.method) return;
        try {
            const currentTip = await myFetch[tipReadAllLink.method](`${tipReadAllLink.href}/link/${link}`);
            if (!currentTip) return;
            setTips((prev) => {
                const alreadyExists = prev.some((t) => t.tipData.slug === currentTip.slug);
                if (alreadyExists) return prev;
                return [...prev, { id: `${currentTip.slug}-${Date.now()}`, tipData: currentTip }];
            });
        } catch (err) {
            displayRequestErrors(err);
        }
    };

    const dismissCurrent = useCallback(() => {
        setTips((prev) => {
            const next = prev.filter((_, i) => i !== currentIndex);
            setCurrentIndex((idx) => Math.min(idx, Math.max(next.length - 1, 0)));
            return next;
        });
    }, [currentIndex]);

    const visibleTips = tips.filter((tip) => tip.tipData?.shortContent?.[locale]);

    if (visibleTips.length === 0) return null;

    const current = visibleTips[currentIndex];
    const tip = current?.tipData;
    const total = visibleTips.length;

    return (
        <Box
            pos="fixed"
            style={{ top: 138, right: 20, zIndex: 99999, width: '360px', maxWidth: 'calc(100vw - 40px)' }}
        >
            <Box
                style={{
                    position: 'relative',
                    animation: slideDir ? `slideIn-${slideDir} 0.25s ease` : undefined,
                }}
            >
                <Notification
                    key={current?.id}
                    title="Astuce"
                    radius="md"
                    color="yellow"
                    icon={<FontAwesomeIcon icon={faLightbulb} />}
                    onClose={dismissCurrent}
                    withBorder
                    styles={{
                        root: {
                            alignItems: 'flex-start',
                            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
                            width: '100%',
                            paddingBottom: total > 1 ? '36px' : 'var(--mantine-spacing-md)',
                        },
                        title: { fontWeight: 'bold' },
                    }}
                >
                    <Text size="sm" fw={700}>
                        {tip?.title?.[locale]}
                    </Text>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: tip?.shortContent?.[locale]
                                ? `${tip.shortContent[locale].replace('\n', '<br />')} <a href="/faq/${tip.slug}" target="_blank" style="color: var(--mantine-color-nli-liberty-5); text-decoration: underline;">${t('learn-more')}</a>`
                                : '',
                        }}
                    />
                </Notification>

                {total > 1 && (
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: '12px',
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            zIndex: 10,
                        }}
                    >
                        {visibleTips.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => {
                                    setSlideDir(i > currentIndex ? 'right' : 'left');
                                    setCurrentIndex(i);
                                    setTimeout(() => setSlideDir(null), 250);
                                }}
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    background: i === currentIndex
                                        ? 'var(--mantine-color-nli-liberty-6)'
                                        : 'var(--mantine-color-gray-4)',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s ease',
                                    flexShrink: 0,
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            <style>
                {`
                @keyframes slideIn-left {
                    from { transform: translateX(20px); opacity: 0; }
                    to   { transform: translateX(0);   opacity: 1; }
                }
                @keyframes slideIn-right {
                    from { transform: translateX(-20px); opacity: 0; }
                    to   { transform: translateX(0);     opacity: 1; }
                }
            `}
            </style>
        </Box>
    );
}